// Copy for /locations/new-york/brooklyn/ and /locations/new-york/queens/
// (StudioLocationPage, code pages since 2026-11-04; gatsby-node skips the
// Contentful location entries). NAP comes from lib/locations.js and
// lib/business.js only: the old CMS pages showed a second phone number and
// opening hours that .seo/truth.md doesn't back ("No verified fixed opening
// schedule"), so visits are "by appointment" and no hours are published.
// Service facts are the published 3D printing, UV and engraving terms
// (./printing-service, truth.md): pickup and drop-off by appointment at
// either studio, NYC courier delivery (Uber courier is fine), shipping to
// all 50 states. Which machines sit in which studio isn't confirmed beyond
// the Queens page's own copy (the larger facility, with large-scale printing,
// CNC milling, laser cutting and scanning), so the Brooklyn page doesn't
// assign machines to the building.
const { locations } = require('../../lib/locations')
const { BUSINESS_PHONE, BUSINESS_EMAIL } = require('../../lib/business')
const printing = require('./printing-service')
const printingNyc = require('./printing-nyc')

const { pricing } = printing
const byId = Object.fromEntries(locations.map((l) => [l.id, l]))

const directionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    address
  )}`

// NYC service pages a visitor to either studio is looking for.
const nycServices = [
  {
    label: '3D printing',
    title: '3D printing in NYC',
    path: '/3d-printing-nyc/',
    body: `Prototypes, models, props and production runs, from about $${pricing.printFrom}. No setup fee.`,
  },
  {
    label: '3D scanning',
    title: '3D scanning in NYC',
    path: '/3d-scanning-nyc/',
    body: `Scan an object to an STL or OBJ, at the studio or on site in New York. From $${pricing.scanningFrom}.`,
  },
  {
    label: 'Laser engraving',
    title: 'Laser engraving in NYC',
    path: '/laser-engraving-service-nyc/',
    body: 'Logos and names on metal, wood, leather and acrylic, on your items or ours.',
  },
  {
    label: 'UV printing',
    title: 'UV printing in NYC',
    path: '/services/uv-printing/ny/new-york/',
    body: 'Full-color logos printed straight onto products, packaging and awards.',
  },
  {
    label: 'Laser cutting',
    title: 'Laser cutting in NYC',
    path: '/laser-cutting-service-nyc/',
    body: 'Acrylic, wood and card cut and engraved, up to 40 × 28 inches.',
  },
  {
    label: 'Events',
    title: 'On-site engraving in NYC',
    path: '/services/on-site-laser-engraving-nyc/',
    body: 'A live engraving station for brand activations and trade shows.',
  },
]

const sharedGlance = (location) => [
  ['Address', location.address],
  ['Visits', 'By appointment only. Confirm the time before you come.'],
  ['Phone', BUSINESS_PHONE.replace(/^\+1-/, '')],
  ['Email', BUSINESS_EMAIL],
  [
    'Pickup & drop-off',
    'By appointment, for your finished work or items you need printed or engraved',
  ],
  [
    'Courier',
    'NYC courier delivery, and an Uber courier is fine for drop-offs and pickups',
  ],
  ['Shipping', 'To all 50 states; next-day shipping available'],
]

const brooklyn = {
  location: byId.brooklyn,
  page: {
    path: '/locations/new-york/brooklyn/',
    title: '3D Printing Brooklyn | PrintAWorld Brooklyn Studio',
    description: `PrintAWorld's Brooklyn studio at 616 Berriman Street: 3D printing, engraving and UV printing for businesses. Pickup by appointment, NYC courier delivery.`,
    heading: '3D Printing in Brooklyn: Our Brooklyn Studio',
    eyebrow: 'Brooklyn 3D print shop · by appointment',
    crumb: 'Brooklyn',
    name: 'PrintAWorld Brooklyn Studio',
    answer: `PrintAWorld’s Brooklyn studio is at 616 Berriman Street, Brooklyn, NY 11208. It’s one of our two New York studios: we 3D print, engrave and UV print for businesses, brands and agencies, with no setup fee for 3D printing and prints from about $${pricing.printFrom}. Visits, drop-offs and pickups are by appointment; we deliver across New York by courier and ship to all 50 states.`,
    hero: 'burger',
    reviewedDate: '2026-11-04',
  },
  photos: printingNyc.photos,
  intro: {
    eyebrow: '3D printing near you',
    heading: 'What can you get made at our Brooklyn studio?',
    paragraphs: [
      'Between our Brooklyn and Queens studios we offer 3D printing from one prototype to production runs on our FDM print farm, resin and stainless-steel metal printing, 3D modeling and scanning, laser engraving and cutting, UV printing, and painting and plating.',
      'You can drop off items for engraving and UV printing here and pick up finished work, by appointment. Your quote names the studio that makes your job, so you know where to collect it.',
      'There’s no counter to walk up to. Send your file or photos first, a person checks them and quotes, and then you book a time. Printing a one-off for fun? A hobbyist or a print marketplace will usually cost you less.',
    ],
  },
  glance: [
    ...sharedGlance(byId.brooklyn),
    [
      'Who it’s for',
      'Businesses, brands, agencies and creative or production work',
    ],
    ['Setup fee', 'None for 3D printing'],
    [
      'Prices',
      `3D printing from about $${pricing.printFrom}; modeling and scanning from $${pricing.modelingFrom}`,
    ],
    [
      'Turnaround',
      'Next-day printing for many jobs; your quote confirms the date',
    ],
  ],
  work: ['busts', 'hands', 'cubes', 'prototype'],
  workHeading: 'Made in our New York studios',
  visit: {
    heading: 'How to get to our Brooklyn studio',
    body: 'We’re near the Brooklyn–Queens border, a short drive from our Glendale studio. Book your visit first: we’ll confirm the time and whether your job is at Brooklyn or Queens.',
  },
  courier: {
    heading: 'Courier pickup and delivery in Brooklyn',
    paragraphs: [
      'Can’t come by? We send finished work across Brooklyn, Manhattan and the rest of New York by courier, and an Uber courier is fine for dropping items off or collecting them.',
      'Outside the city we ship to all 50 states, with next-day shipping once your job is ready.',
    ],
  },
  faqs: [
    {
      question: 'Is there a 3D printing shop in Brooklyn?',
      answer: `Yes. PrintAWorld has a studio at 616 Berriman Street, Brooklyn, NY 11208, and another in Glendale, Queens. We 3D print prototypes, models, props and production runs for businesses, from about $${pricing.printFrom} with no setup fee. Visits are by appointment, so send your file first.`,
    },
    {
      question: 'What are the Brooklyn studio’s hours?',
      answer:
        'We work by appointment rather than set walk-in hours. Send your file or photos, get your quote, and we’ll book a time for your drop-off or pickup.',
    },
    {
      question: 'Can I pick up my 3D print in Brooklyn?',
      answer:
        'Yes, by appointment, when your quote names the Brooklyn studio. If your job is made in Queens, you can collect it there or we can send it to you by courier.',
    },
    {
      question:
        'Can I drop off items for engraving or UV printing in Brooklyn?',
      answer:
        'Yes, by appointment once your quote is approved. Send the item details first so we can test and plan the job; an Uber courier is fine for the drop-off.',
    },
    {
      question: 'Do you do metal 3D printing in Brooklyn?',
      answer:
        'We print stainless steel by SLM, up to 250 × 250 × 300 mm, for clients across New York. Metal parts are quoted per project after we review the file.',
    },
    {
      question: 'Do you deliver 3D prints in Brooklyn and Manhattan?',
      answer:
        'Yes. We deliver across New York City by courier, and many jobs can be printed next day. Same-day isn’t standard; your quote confirms the date.',
    },
    {
      question: 'Who does the Brooklyn studio work with?',
      answer:
        'Businesses, brands, agencies, architects and creative or production teams. For a cheap hobby print or parts to resell, a hobbyist or marketplace will usually cost less.',
    },
  ],
  otherStudio: 'queens',
}

const queens = {
  location: byId.glendale,
  page: {
    path: '/locations/new-york/queens/',
    title: 'PrintAWorld Factory | 3D Printing Studio in Queens, NY',
    description: `PrintAWorld Factory, our Queens studio at 74-12 88th Street, Glendale: 3D printing, large-format prints, CNC, laser cutting and scanning, by appointment.`,
    heading: 'PrintAWorld Factory: 3D Printing Studio in Queens',
    eyebrow: 'Glendale, Queens · by appointment',
    crumb: 'Queens',
    name: 'PrintAWorld Factory',
    answer: `PrintAWorld Factory is our studio at 74-12 88th Street, Glendale, NY 11385, in Queens. It’s our biggest facility, with large-scale 3D printing, CNC milling, laser cutting and 3D scanning alongside our 3D printers. We make prototypes, models, props and production runs for businesses, from about $${pricing.printFrom} with no setup fee for 3D printing. Visits are by appointment; we deliver across New York by courier and ship to all 50 states.`,
    hero: 'carFrame',
    reviewedDate: '2026-11-04',
  },
  photos: printingNyc.photos,
  intro: {
    eyebrow: '3D printing in Queens',
    heading: 'What do we make at PrintAWorld Factory in Queens?',
    paragraphs: [
      'We moved into Glendale because we needed room: for large-scale 3D printing, CNC milling, laser cutting and 3D scanning, and for showing clients their work. It’s where we build large pieces, like props and models of 6 feet and more printed in sections.',
      'From here we print prototypes, architectural and scale models, props and production runs on our FDM print farm and resin printers, and sand, paint and plate them.',
      'Visits are by appointment, not walk-in. Send your file or photos first; a person reviews them and quotes, and we book your time at the studio.',
    ],
  },
  glance: [
    ...sharedGlance(byId.glendale),
    [
      'At this studio',
      'Large-scale 3D printing, CNC milling, laser cutting and 3D scanning, plus our 3D printers',
    ],
    ['Largest size', '6 feet and more, printed in sections'],
    ['Setup fee', 'None for 3D printing'],
    [
      'Prices',
      `3D printing from about $${pricing.printFrom}; modeling and scanning from $${pricing.modelingFrom}`,
    ],
  ],
  work: ['building', 'clocks', 'stairs', 'chrome'],
  workHeading: 'Made at our New York studios',
  visit: {
    heading: 'How to get to PrintAWorld Factory in Glendale',
    body: 'We’re in Glendale, Queens, near the Brooklyn border and a short drive from our Brooklyn studio. Book your visit first, and we’ll confirm the time.',
  },
  courier: {
    heading: 'Courier delivery from Queens to all of NYC',
    paragraphs: [
      'Large builds and finished pieces go across Queens, Manhattan, Brooklyn and the rest of New York City by courier, and an Uber courier is fine for smaller drop-offs and pickups.',
      'Anywhere else, we ship to all 50 states, with next-day shipping once your job is ready.',
    ],
  },
  faqs: [
    {
      question: 'What is PrintAWorld Factory?',
      answer:
        'It’s what we call our Queens studio at 74-12 88th Street, Glendale, NY 11385: our biggest facility, with large-scale 3D printing, CNC milling, laser cutting and 3D scanning. Visits are by appointment.',
    },
    {
      question: 'Is there a 3D printing shop in Queens?',
      answer: `Yes. PrintAWorld Factory in Glendale, Queens prints prototypes, models, props and production runs for businesses, from about $${pricing.printFrom} with no setup fee. Send your file first and we’ll book your visit.`,
    },
    {
      question: 'Can I visit the Queens studio without an appointment?',
      answer:
        'Please don’t: we don’t keep walk-in hours. Book a time after your quote, and we’ll have your job or your consultation ready.',
    },
    {
      question: 'Can you 3D print large objects in Queens?',
      answer:
        'Yes. Large-scale prints, props and models are built at this studio, in sections and assembled for pieces of 6 feet and more. Large-format jobs usually take 1–2 weeks.',
    },
    {
      question: 'Can you 3D scan an object at the Queens studio?',
      answer: `Yes. Bring the object by appointment, or for something too big or fragile to move we can scan on site in the New York area. Scanning starts at $${pricing.scanningFrom}.`,
    },
    {
      question: 'Do you deliver from Queens to Manhattan?',
      answer:
        'Yes, by courier across New York City. Many jobs can be printed next day; same-day isn’t standard. Your quote confirms the date.',
    },
    {
      question: 'Which studio should I use, Queens or Brooklyn?',
      answer:
        'You don’t have to choose. Send your project and your quote names the studio that makes it; pick up there by appointment, or have it delivered.',
    },
  ],
  otherStudio: 'brooklyn',
}

module.exports = {
  pricing,
  nycServices,
  directionsUrl,
  studios: { brooklyn, queens },
}
